import { currentRuntime } from '../../config/runtime';
import type { TenantId } from '../../config/runtime/types';
import { getCanonicalAssetClass, type CanonicalAssetClass } from './canonicalAssetClass';

type SupportedAssetClass = Exclude<CanonicalAssetClass, 'UNKNOWN'>;

const ALL_SUPPORTED_ASSET_CLASSES: readonly SupportedAssetClass[] = [
  'STOCKS',
  'FUTURES',
  'FOREX',
  'CRYPTO',
];

// Fallback defaults used ONLY when no dynamic tenant policy has been installed
// (e.g. before the server-provided white-label launch controls / tenant launch
// controls arrive). Warrior is an equities-only experience; the core
// Trade-Proof tenant continues to expose every supported asset class.
const WARRIOR_DEFAULT_ASSET_CLASSES: readonly SupportedAssetClass[] = ['STOCKS'];

const DEFAULT_ASSET_CLASSES_BY_TENANT: ReadonlyMap<string, readonly SupportedAssetClass[]> = new Map([
  ['warrior-trading', WARRIOR_DEFAULT_ASSET_CLASSES],
  ['warriortrading', WARRIOR_DEFAULT_ASSET_CLASSES],
]);

// ---------------------------------------------------------------------------
// Runtime tenant asset policy.
//
// The authoritative enabled-asset-classes come from the tenant configuration
// (white-label launch_controls / tenant_launch_controls) which the app receives
// over the network. Because trade data is read synchronously, the fetched policy
// is installed here and consulted by every display boundary. Until it arrives,
// the deterministic default mapping above applies — never a per-page decision.
// ---------------------------------------------------------------------------

const policyOverrides = new Map<string, readonly SupportedAssetClass[]>();
let activeTenantSlugOverride: string | null = null;

const normalizeSlug = (value: string | null | undefined) => String(value ?? '').trim().toLowerCase();

/**
 * Installs the tenant's enabled asset classes from server-provided config.
 * Keyed by the tenant slug so dynamic white-label tenants (which run on the
 * shared default shell) enforce their own entitlement set.
 */
export function setTenantDisplayAssetClasses(
  tenantSlugOrId: string | null | undefined,
  enabledAssetClasses: Array<SupportedAssetClass | string> | readonly SupportedAssetClass[] | null | undefined,
): void {
  const slug = normalizeSlug(tenantSlugOrId);
  if (!slug) return;

  const normalized = Array.from(enabledAssetClasses ?? [])
    .map((entry) => String(entry ?? '').trim().toUpperCase())
    .filter((entry): entry is SupportedAssetClass => {
      return (ALL_SUPPORTED_ASSET_CLASSES as readonly string[]).includes(entry);
    });

  if (normalized.length === 0) return;
  policyOverrides.set(slug, Array.from(new Set(normalized)));
}

/**
 * Marks the tenant slug the user is currently operating in. Used by the
 * synchronous trade boundaries so a dynamic white-label hostname resolves the
 * correct tenant asset policy even though the shared shell is the runtime.
 */
export function setActiveTenantSlugForDisplayScope(tenantSlug: string | null | undefined): void {
  activeTenantSlugOverride = normalizeSlug(tenantSlug) || null;
}

export function resetTenantDisplayAssetPolicy(): void {
  policyOverrides.clear();
  activeTenantSlugOverride = null;
}

/**
 * Product display policy, intentionally separate from importer support and
 * replay controls. Resolution order:
 *   1. an installed dynamic policy for the explicit tenant reference;
 *   2. an installed dynamic policy for the active tenant slug;
 *   3. the deterministic default mapping for the runtime tenant.
 */
export function getTenantDisplayAssetClasses(
  tenantRef?: TenantId | string,
): readonly SupportedAssetClass[] {
  const explicitSlug = normalizeSlug(tenantRef);
  if (explicitSlug && policyOverrides.has(explicitSlug)) {
    return policyOverrides.get(explicitSlug) as readonly SupportedAssetClass[];
  }

  // An explicit tenant reference resolves against the deterministic default
  // mapping before the runtime tenant is considered.
  if (explicitSlug && DEFAULT_ASSET_CLASSES_BY_TENANT.has(explicitSlug)) {
    return DEFAULT_ASSET_CLASSES_BY_TENANT.get(explicitSlug) as readonly SupportedAssetClass[];
  }

  const activeSlug = normalizeSlug(activeTenantSlugOverride);
  if (activeSlug && policyOverrides.has(activeSlug)) {
    return policyOverrides.get(activeSlug) as readonly SupportedAssetClass[];
  }

  const runtimeTenant = normalizeSlug(currentRuntime.tenant.id);
  return DEFAULT_ASSET_CLASSES_BY_TENANT.get(runtimeTenant) ?? ALL_SUPPORTED_ASSET_CLASSES;
}

export function isTenantAssetClassEnabled(
  assetClass: CanonicalAssetClass,
  tenantRef?: TenantId | string,
): boolean {
  if (assetClass === 'UNKNOWN') return false;
  return getTenantDisplayAssetClasses(tenantRef).includes(assetClass as SupportedAssetClass);
}

export function isTradeWithinTenantDisplayScope(
  trade: unknown,
  tenantRef?: TenantId | string,
): boolean {
  const assetClass = getCanonicalAssetClass(trade);

  // Preserve the core tenant's legacy/manual records when they do not carry
  // enough information to determine an asset class. Tenants with a strict
  // policy (e.g. stocks-only) must fail closed on unclassified records.
  if (assetClass === 'UNKNOWN') {
    return getTenantDisplayAssetClasses(tenantRef).length === ALL_SUPPORTED_ASSET_CLASSES.length;
  }

  return isTenantAssetClassEnabled(assetClass, tenantRef);
}

export function filterTradesForTenantDisplay<T>(
  trades: T[],
  tenantRef?: TenantId | string,
): T[] {
  if (!Array.isArray(trades)) return [];
  return trades.filter((trade) => isTradeWithinTenantDisplayScope(trade, tenantRef));
}

const ASSET_CLASS_LABELS: Record<SupportedAssetClass, string> = {
  STOCKS: 'Stocks',
  FUTURES: 'Futures',
  FOREX: 'Forex',
  CRYPTO: 'Crypto',
};

type LaunchControlsLike = {
  stocks?: boolean;
  futures?: boolean;
  forex?: boolean;
  crypto?: boolean;
};

/**
 * Converts the white-label launch_controls booleans (the tenant's configured
 * product asset switches) into the enabled asset-class list. Mirrors the server
 * serializer semantics (stocks default on; others default off).
 */
export function getTenantDisplayAssetClassesFromLaunchControls(
  launchControls: LaunchControlsLike | null | undefined,
): SupportedAssetClass[] {
  if (!launchControls || typeof launchControls !== 'object') return [];
  const enabled: SupportedAssetClass[] = [];
  if (launchControls.stocks !== false) enabled.push('STOCKS');
  if (launchControls.futures === true) enabled.push('FUTURES');
  if (launchControls.forex === true) enabled.push('FOREX');
  if (launchControls.crypto === true) enabled.push('CRYPTO');
  return enabled;
}

/**
 * Friendly, tenant-driven rejection message for a trade whose asset class is
 * not enabled for the current tenant. Returns null when the trade is allowed.
 */
export function getTenantAssetBlockedMessage(
  trade: unknown,
  tenantRef?: TenantId | string,
): string | null {
  if (isTradeWithinTenantDisplayScope(trade, tenantRef)) return null;

  const assetClass = getCanonicalAssetClass(trade);
  const label = assetClass !== 'UNKNOWN' ? ASSET_CLASS_LABELS[assetClass] ?? assetClass : 'this asset class';
  return `${label} trading is not enabled for this workspace.`;
}
